import {CSSProperties, useEffect, useRef, useState} from "react";
import {Button} from "react-bootstrap";
import {ProductDemoViewModel} from "../../api/models";
import {format, products} from "../../i18n";
import {Spectrum, useDemoPicture, Waveform} from "./DemoVisuals";

const SKIP_SECONDS = 10;
// Where the volume starts, where the page can set it: demos at full volume can startle.
const DEFAULT_VOLUME = 0.5;
// Previous restarts the demo once it's this far in, as players usually do.
const RESTART_AFTER_SECONDS = 3;

// 83 → "1:23".
export function formatTime(seconds: number): string {
    const whole = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

// Whether the page can set the volume. iOS and iPadOS leave it to the hardware buttons: setting it does nothing. Older
// versions read it back as 1, which the probe catches; newer ones (iOS 26) echo the value while still ignoring it, so
// those are recognised by name too. An iPad's Safari calls itself a Mac, but Macs have no touch screen.
function isAppleMobile(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function canSetVolume(): boolean {
    if (isAppleMobile()) {
        return false;
    }
    try {
        const probe = document.createElement('audio');
        probe.volume = 0.5;
        return probe.volume === 0.5;
    } catch {
        return true;
    }
}

// The product's audio demos, in a player of the site's own: an <audio> element without controls (so the browser
// shows nothing) does the playing. The sliders are native range inputs, restyled, so the keyboard and screen readers
// work them as usual. Nothing plays until the visitor asks. A demo with an analysis (DemoVisuals.tsx) also shows its
// spectrum, moving with the music, and its waveform under the seek slider, which is then drawn as a playhead.
export function DemoPlayer({productId, demos}: { productId: string, demos: ProductDemoViewModel[] }) {
    const audio = useRef<HTMLAudioElement>(null);
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [time, setTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volumeAdjustable] = useState(canSetVolume);
    const [volume, setVolume] = useState(volumeAdjustable ? DEFAULT_VOLUME : 1);
    const [muted, setMuted] = useState(false);
    const [failed, setFailed] = useState(false);
    // Whether the next demo loaded should start playing: after Next while playing, a click in the list, or the end of
    // the one before.
    const playWhenLoaded = useRef(false);

    const current = Math.min(index, demos.length - 1);
    const demo = demos[current];
    const picture = useDemoPicture(productId, demo);

    // The element keeps its volume from one demo to the next, so this is only needed once.
    useEffect(() => {
        if (volumeAdjustable && audio.current) {
            audio.current.volume = DEFAULT_VOLUME;
        }
    }, [volumeAdjustable]);

    // A new demo starts from nothing. Not on mounting, when that's already so: the first demo's metadata may have
    // arrived before this effect runs, and resetting would lose its duration.
    const shownUrl = useRef(demo?.url);
    useEffect(() => {
        if (shownUrl.current === demo?.url) {
            return;
        }
        shownUrl.current = demo?.url;
        setTime(0);
        setDuration(0);
        setFailed(false);
        if (playWhenLoaded.current) {
            play();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demo?.url]);

    if (!demo) {
        return null;
    }

    function play() {
        // Refused (no user gesture, or a format the browser can't play): the error and pause events say so.
        audio.current?.play()?.catch(() => setPlaying(false));
    }

    const toggle = () => {
        const element = audio.current;
        if (!element) {
            return;
        }
        if (element.paused) {
            playWhenLoaded.current = true;
            play();
        } else {
            playWhenLoaded.current = false;
            element.pause();
        }
    };

    const go = (to: number, andPlay: boolean) => {
        playWhenLoaded.current = andPlay;
        if (to === current) {
            if (andPlay) {
                play();
            }
        } else {
            setIndex(to);
        }
    };

    const seekTo = (seconds: number) => {
        const element = audio.current;
        if (!element) {
            return;
        }
        const target = Math.max(0, Math.min(seconds, duration || element.duration || 0));
        element.currentTime = target;
        setTime(target);
    };

    const previous = () => {
        if (time > RESTART_AFTER_SECONDS || current === 0) {
            seekTo(0);
        } else {
            go(current - 1, playing);
        }
    };

    const changeVolume = (value: number) => {
        const element = audio.current;
        if (!element) {
            return;
        }
        element.volume = value;
        element.muted = value === 0;
        setVolume(value);
        setMuted(value === 0);
    };

    const toggleMute = () => {
        const element = audio.current;
        if (!element) {
            return;
        }
        const mute = !muted;
        element.muted = mute;
        // Unmuting at zero would stay silent.
        if (!mute && element.volume === 0) {
            element.volume = DEFAULT_VOLUME;
            setVolume(DEFAULT_VOLUME);
        }
        setMuted(mute);
    };

    const fill = (value: number, max: number): CSSProperties => ({ '--fill': `${max > 0 ? (value / max) * 100 : 0}%` } as CSSProperties);
    const shownVolume = muted ? 0 : volume;
    // Over the waveform, when there is one, as just its playhead.
    const seek = (
        <input type="range" className={`demo-player-range${picture ? ' demo-player-playhead' : ' flex-grow-1'}`} min={0} max={duration || 0} step="any"
               value={Math.min(time, duration || 0)} disabled={!duration} aria-label={products.demos.seek}
               aria-valuetext={format(products.demos.position, { elapsed: formatTime(time), total: formatTime(duration) })}
               style={fill(time, duration)} onChange={e => seekTo(Number(e.target.value))}/>
    );

    return (
        <div className="demo-player">
            <audio ref={audio} src={demo.url} preload="metadata"
                   onPlay={() => setPlaying(true)}
                   onPause={() => setPlaying(false)}
                   onTimeUpdate={e => setTime(e.currentTarget.currentTime)}
                   onLoadedMetadata={e => setDuration(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
                   onDurationChange={e => setDuration(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
                   onVolumeChange={e => { setVolume(e.currentTarget.volume); setMuted(e.currentTarget.muted); }}
                   onError={() => { setFailed(true); setPlaying(false); }}
                   onEnded={() => {
                       if (current < demos.length - 1) {
                           go(current + 1, true);
                       } else {
                           playWhenLoaded.current = false;
                           setPlaying(false);
                       }
                   }}/>

            <div aria-live="polite" className="mb-2">
                <div className="d-flex align-items-baseline gap-2">
                    <strong className="demo-player-title">{demo.title}</strong>
                    {demos.length > 1 && (
                        <span className="small text-body-secondary ms-auto text-nowrap">
                            {format(products.demos.trackOf, { number: current + 1, count: demos.length })}
                        </span>
                    )}
                </div>
                {demo.description && <p className="small text-body-secondary product-text mb-0">{demo.description}</p>}
            </div>
            {failed && <p className="small text-danger mb-2">{products.demos.cantPlay}</p>}

            {picture && <Spectrum picture={picture} audio={audio} playing={playing}/>}
            <div className="d-flex align-items-center gap-2">
                <span className="demo-player-time" aria-hidden="true">{formatTime(time)}</span>
                {picture ? (
                    <div className="demo-player-scrub flex-grow-1">
                        <Waveform peaks={picture.peaks} played={duration ? time / duration : 0}/>
                        {seek}
                    </div>
                ) : seek}
                <span className="demo-player-time" aria-hidden="true">{formatTime(duration)}</span>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-1 mt-2">
                <div className="d-flex align-items-center gap-1">
                    <Button variant="link" className="demo-player-button" aria-label={products.demos.previous} disabled={current === 0 && time <= RESTART_AFTER_SECONDS} onClick={previous}>
                        <i className="bi bi-skip-start-fill" aria-hidden="true"></i>
                    </Button>
                    <Button variant="link" className="demo-player-button" aria-label={products.demos.back} disabled={!duration} onClick={() => seekTo(time - SKIP_SECONDS)}>
                        <i className="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                    </Button>
                    <Button variant="primary" className="demo-player-play rounded-circle" aria-label={playing ? products.demos.pause : products.demos.play} onClick={toggle}>
                        <i className={`bi ${playing ? 'bi-pause-fill' : 'bi-play-fill'}`} aria-hidden="true"></i>
                    </Button>
                    <Button variant="link" className="demo-player-button" aria-label={products.demos.forward} disabled={!duration} onClick={() => seekTo(time + SKIP_SECONDS)}>
                        <i className="bi bi-arrow-clockwise" aria-hidden="true"></i>
                    </Button>
                    <Button variant="link" className="demo-player-button" aria-label={products.demos.next} disabled={current === demos.length - 1} onClick={() => go(current + 1, playing)}>
                        <i className="bi bi-skip-end-fill" aria-hidden="true"></i>
                    </Button>
                </div>
                <div className="d-flex align-items-center gap-1 ms-auto">
                    <Button variant="link" className="demo-player-button" aria-label={muted ? products.demos.unmute : products.demos.mute} aria-pressed={muted} onClick={toggleMute}>
                        <i className={`bi ${shownVolume === 0 ? 'bi-volume-mute-fill' : shownVolume < 0.5 ? 'bi-volume-down-fill' : 'bi-volume-up-fill'}`} aria-hidden="true"></i>
                    </Button>
                    {volumeAdjustable && (
                        <input type="range" className="demo-player-range demo-player-volume" min={0} max={1} step={0.05} value={shownVolume}
                               aria-label={products.demos.volume} aria-valuetext={`${Math.round(shownVolume * 100)}%`}
                               style={fill(shownVolume, 1)} onChange={e => changeVolume(Number(e.target.value))}/>
                    )}
                </div>
            </div>

            {demos.length > 1 && (
                <ol className="demo-player-list list-unstyled mt-3 mb-0" aria-label={products.demos.list}>
                    {demos.map((x, i) => (
                        <li key={x.id}>
                            <button type="button" className="demo-player-track" aria-current={i === current ? 'true' : undefined} onClick={() => go(i, true)}>
                                <span className="demo-player-track-number" aria-hidden="true">
                                    {i === current && playing ? <i className="bi bi-volume-up-fill"></i> : i + 1}
                                </span>
                                {x.title}
                            </button>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}
